<template>
  <div style="border: 1px solid #bfbfbf; border-radius: 24px">
    <!-- Clickable Header -->
    <div
      class="d-flex align-center justify-space-between py-6 px-6"
      style="cursor: pointer"
      role="button"
      tabindex="0"
      :aria-expanded="expanded ? 'true' : 'false'"
      :aria-controls="groupId"
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
    >
      <div class="flex-grow-1">
        <!-- Badge + Credits -->
        <div class="d-flex align-center gap-3 mb-1">
          <span class="text-caption font-weight-bold text-uppercase" :style="badgeStyles">
            {{ badgeLabel }}
          </span>
          <span class="text-body-2" style="color: #666666; white-space: nowrap"> {{ credits }} credits </span>
        </div>
        <!-- Choose one summary -->
        <div class="text-body-2 font-weight-bold" style="color: #000">
          <span class="font-weight-bold">Choose one:</span> {{ prefixSummary }}
        </div>
      </div>
      <!-- Chevron -->
      <v-icon color="#747474" class="ms-3">
        {{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
      </v-icon>
    </div>

    <!-- Expanded Course Cards -->
    <v-expand-transition>
      <div v-if="expanded" :id="groupId">
        <div class="px-4 pb-4 d-flex flex-column">
          <slot />
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

// Highest-progress badge in the group wins the header label
const BADGE_PRIORITY = [
  'COMPLETED',
  'IN PROGRESS',
  'ENROLLED',
  'SCHEDULED',
  'TRANSFER CREDIT',
  'PREREQ REQUIRED',
  'RECOMMENDED',
  'REQUIRED'
];

export default defineComponent({
  name: 'CourseOrGroup',

  props: {
    courses: {
      type: Array,
      required: true
    },
    defaultLabel: {
      type: String,
      default: 'REQUIRED'
    }
  },

  data() {
    return {
      expanded: false
    };
  },

  computed: {
    // Ties the header's aria-controls to the panel it expands
    groupId() {
      return `course-or-group-${this.courses.map((c) => c.coursePrefix).join('-')}`.replace(/\s+/g, '');
    },

    credits() {
      return this.courses[0]?.credits || 3;
    },

    badgeLabel() {
      let bestLabel = this.defaultLabel;
      let bestPriority = BADGE_PRIORITY.length;

      for (const course of this.courses) {
        const label = course.badge?.text || this.defaultLabel;
        const priority = BADGE_PRIORITY.indexOf(label);
        if (priority !== -1 && priority < bestPriority) {
          bestPriority = priority;
          bestLabel = label;
        }
      }
      return bestLabel;
    },

    badgeStyles() {
      const matchingCourse = this.courses.find((c) => c.badge?.text === this.badgeLabel);
      const bg = matchingCourse?.badge?.color || '#e74973';
      const textColor = matchingCourse?.badge?.textColor || '#ffffff';
      return `background-color: ${bg}; color: ${textColor} !important; font-weight: 700; letter-spacing: 0.5px; padding: 1px 14px; border-radius: 4px; white-space: nowrap;`;
    },

    prefixSummary() {
      const prefixes = this.courses.map((c) => c.coursePrefix);
      if (prefixes.length <= 1) return prefixes[0] || '';
      return prefixes.slice(0, -1).join(', ') + ' or ' + prefixes[prefixes.length - 1];
    }
  },

  methods: {
    toggle() {
      this.expanded = !this.expanded;
    }
  }
});
</script>

<style scoped>
.gap-3 {
  gap: 12px;
}
</style>
